import c from "classnames";
import { useState } from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { Responsive } from "react-grid-layout";

import {
  ClientApplicationLastUsedStat,
  ClientExtensionAdoptionRateStat,
  ClientTotalUsersStat,
} from "@components/Stats";
import {
  ApplicationsMostUsedByClientTable,
  ClientEmployeesMostActiveTable,
} from "@components/Tables";

import { Card } from "@design-system/Card/Card";

import classes from "./ClientDashboard.module.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const layout = [
  { i: "a", x: 0, y: 0, w: 4, h: 4 },
  { i: "b", x: 4, y: 0, w: 4, h: 4 },
  { i: "c", x: 8, y: 0, w: 4, h: 4 },
  { i: "e", x: 0, y: 4, w: 6, h: 16 },
  { i: "f", x: 6, y: 4, w: 6, h: 16 },
];

const layoutMd = [
  { i: "a", x: 0, y: 0, w: 12, h: 4 },
  { i: "b", x: 0, y: 4, w: 6, h: 4 },
  { i: "c", x: 6, y: 4, w: 6, h: 4 },
  { i: "e", x: 0, y: 8, w: 12, h: 16 },
  { i: "f", x: 0, y: 24, w: 12, h: 16 },
];

const layoutSm = [
  { i: "a", x: 0, y: 0, w: 12, h: 4 },
  { i: "b", x: 0, y: 4, w: 12, h: 4 },
  { i: "c", x: 0, y: 8, w: 12, h: 4 },
  { i: "e", x: 0, y: 12, w: 12, h: 16 },
  { i: "f", x: 0, y: 28, w: 12, h: 16 },
];

const layouts = {
  lg: layout,
  md: layout,
  sm: layoutMd,
  xs: layoutMd,
  xxs: layoutSm,
};

type Props = {
  clientId: string;
};

export const ClientDashboard = ({ clientId }: Props) => {
  const ref1 = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [dynamicLayouts, setDynamicLayouts] = useState(layouts);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- enable once we have a way to edit the layout
  const [isEditingLayout, _setIsEditingLayout] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (ref1.current) {
        const offsetWidth = ref1.current.offsetWidth;
        setWidth(offsetWidth);

        if (offsetWidth >= 996) {
          const rect = ref1.current.getBoundingClientRect();
          // Calculate available height: window height minus top offset minus bottom padding (24px)
          const availableHeight = window.innerHeight - rect.top - 32;
          
          // Fixed rowHeight = 12, rowGap = 10
          // rowCount * 12 + (rowCount - 1) * 10 = availableHeight
          // 22 * rowCount - 10 = availableHeight
          const availableRows = Math.floor((availableHeight + 10) / 22);

          // Top cards are h: 4. Ensure we have at least min rows for the bottom cards
          const remainingRows = Math.max(16, availableRows - 4);
          
          setDynamicLayouts({
            ...layouts,
            lg: [
              ...layout.slice(0, 3), // a, b, c
              { ...layout[3], h: remainingRows }, // e
              { ...layout[4], h: remainingRows }, // f
            ]
          });
        } else {
          setDynamicLayouts(layouts);
        }
      }
    };

    const observer = new ResizeObserver(handleResize);
    if (ref1.current) {
      observer.observe(ref1.current);
    }
    
    window.addEventListener("resize", handleResize);
    handleResize(); // initial set

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className={classes.container} ref={ref1}>
      <Responsive
        layouts={dynamicLayouts}
        className={classes.responsive}
        rowHeight={12}
        width={width}
        cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
        isResizable={isEditingLayout}
        isDraggable={isEditingLayout}
        autoSize
        containerPadding={[0, 0]}
      >
        <div key="a" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ClientApplicationLastUsedStat clientId={clientId} />
          </div>
        </div>

        <div key="b" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ClientTotalUsersStat clientId={clientId} />
          </div>
        </div>

        <div key="c" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <ClientExtensionAdoptionRateStat clientId={clientId} />
          </div>
        </div>

        <div key="e" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <Card>
              <Card.Header>
                <Card.Header.Title>Most used applications</Card.Header.Title>
              </Card.Header>
              <Card.Body grow>
                <ApplicationsMostUsedByClientTable clientId={clientId} />
              </Card.Body>
            </Card>
          </div>
        </div>

        <div key="f" className={classes.card}>
          <div className={c({ [classes.editing]: isEditingLayout })}>
            <Card>
              <Card.Header>
                <Card.Header.Title>Most active users</Card.Header.Title>
              </Card.Header>
              <Card.Body grow>
                <ClientEmployeesMostActiveTable clientId={clientId} />
              </Card.Body>
            </Card>
          </div>
        </div>
      </Responsive>
    </div>
  );
};
